import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AssessmentData } from "./Assessment";
import { 
  Trophy, Zap, Target, ArrowRight, Mail, Sparkles, 
  Brain, Users, Gauge, AlertTriangle, BarChart3, 
  Calendar, BookOpen, Lightbulb, Shield, Download, FileText, Printer, Loader2
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { escapeHtml, sanitizeFullHtml } from "@/lib/sanitize";

interface StrategyElement {
  element: string;
  description: string;
  psychological_basis?: string;
  priority: string;
  implementation_tips?: string;
  caution_notes?: string;
}

interface AntiPattern {
  pattern: string;
  reason: string;
}

interface Metric {
  metric: string;
  why: string;
  target?: string;
}

interface TimelinePhase {
  weeks: string;
  focus: string;
  elements: string[];
  success_criteria: string;
}

interface ResearchReference {
  concept: string;
  source: string;
  application: string;
}

interface Strategy {
  recommended_tier: string;
  tier_justification: string;
  psychological_profile?: {
    primary_octalysis_drives: string[];
    bartle_type: string;
    sdt_focus: string;
    flow_considerations: string;
  };
  app_profile_summary?: {
    app_type: string;
    target_frequency: string;
    session_length: string;
    key_motivations: string[];
    primary_challenge: string;
    sensory_style?: string[];
  };
  core_elements?: StrategyElement[];
  moderate_elements?: StrategyElement[];
  advanced_elements?: StrategyElement[];
  anti_patterns_to_avoid?: AntiPattern[];
  key_considerations?: string[];
  metrics_to_track?: Metric[];
  implementation_timeline?: {
    phase_1?: TimelinePhase;
    phase_2?: TimelinePhase;
    phase_3?: TimelinePhase;
  };
  personalization_notes?: string;
  research_references?: ResearchReference[];
}

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');
  
  // State from navigation
  const stateData = location.state as {
    assessmentData: AssessmentData;
    submitted: boolean;
    strategy?: Strategy;
    strategyPdfUrl?: string;
    strategyJsonUrl?: string;
  } | null;

  // State for loading from token
  const [loading, setLoading] = useState(!!tokenFromUrl && !stateData);
  const [assessmentData, setAssessmentData] = useState<AssessmentData | null>(stateData?.assessmentData || null);
  const [strategy, setStrategy] = useState<Strategy | null>(stateData?.strategy || null);
  const [strategyPdfUrl, setStrategyPdfUrl] = useState<string | null>(stateData?.strategyPdfUrl || null);
  const [strategyJsonUrl, setStrategyJsonUrl] = useState<string | null>(stateData?.strategyJsonUrl || null);
  const submitted = stateData?.submitted ?? !!tokenFromUrl;

  // Load data from token if accessed via URL
  useEffect(() => {
    const loadFromToken = async () => {
      if (!tokenFromUrl || stateData) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('validate-token', {
          body: { token: tokenFromUrl }
        });

        if (error || !data?.valid) {
          toast.error('Invalid or expired access link');
          navigate('/');
          return;
        }

        // Set assessment data from the response
        const assessment = data.assessment;
        setAssessmentData({
          name: assessment.name || '',
          email: assessment.email,
          appType: assessment.app_type || '',
          experienceIntent: assessment.experience_intent || '',
          usageFrequency: assessment.usage_frequency || '',
          sessionDuration: assessment.session_duration || 0,
          userMotivations: assessment.user_motivations || [],
          mainChallenge: assessment.main_challenge || '',
          sensoryPreferences: assessment.sensory_preferences || [],
        });

        // Set strategy from claude_response
        if (assessment.claude_response) {
          setStrategy(assessment.claude_response as Strategy);
        }
        
        setStrategyPdfUrl(assessment.strategy_pdf_url || null);
        setStrategyJsonUrl(assessment.strategy_json_url || null);
      } catch (err) {
        console.error('Error loading from token:', err);
        toast.error('Failed to load your strategy');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadFromToken();
  }, [tokenFromUrl, stateData, navigate]);

  useEffect(() => {
    if (!loading && !assessmentData && !tokenFromUrl) {
      navigate("/");
    }
  }, [assessmentData, navigate, loading, tokenFromUrl]);

  // Show loading state when fetching from token
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 shadow-2xl border-2 border-primary/20 text-center space-y-6">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
          <h2 className="text-xl font-semibold">Loading Your Strategy...</h2>
          <p className="text-muted-foreground">Please wait while we retrieve your personalized gamification strategy.</p>
        </Card>
      </div>
    );
  }

  if (!assessmentData) {
    return null;
  }

  // Show enhanced results if strategy is available, otherwise show generating screen
  if (submitted && !strategy) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
        <Card className="max-w-3xl w-full p-12 shadow-2xl border-2 border-primary/20 text-center space-y-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 mx-auto animate-in zoom-in duration-500">
            <Sparkles className="w-12 h-12 text-primary" />
          </div>

          <div className="space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold animate-in fade-in slide-in-from-bottom-4 duration-700">
              Generating Your Strategy...
            </h1>
          </div>

          <div className="bg-primary/5 rounded-lg p-8 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
            <div className="flex items-center justify-center gap-3">
              <Mail className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold">Check Your Email</h2>
            </div>
            <p className="text-muted-foreground">
              You&apos;ll receive your complete strategy package via email within 2 minutes at:
            </p>
            <p className="text-lg font-semibold text-primary">{assessmentData.email}</p>
          </div>

          <Button 
            size="lg"
            onClick={() => navigate("/")}
            className="bg-gradient-to-r from-primary to-accent hover:opacity-90 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-400"
          >
            Return to Home
          </Button>
        </Card>
      </div>
    );
  }

  const tierConfig = {
    core: { icon: Target, color: "text-primary", bg: "from-primary/20 to-primary/5", title: "Core Tier" },
    core_moderate: { icon: Zap, color: "text-accent", bg: "from-accent/20 to-accent/5", title: "Core + Moderate Tier" },
    full_advanced: { icon: Trophy, color: "text-yellow-500", bg: "from-yellow-500/20 to-yellow-500/5", title: "Full Advanced Tier" },
    moderate: { icon: Zap, color: "text-accent", bg: "from-accent/20 to-accent/5", title: "Moderate Tier" },
    high: { icon: Trophy, color: "text-yellow-500", bg: "from-yellow-500/20 to-yellow-500/5", title: "High Tier" },
  };

  const tier = (strategy?.recommended_tier || "core") as keyof typeof tierConfig;
  const currentTier = tierConfig[tier] || tierConfig.core;
  const Icon = currentTier.icon;

  const handleDownloadJSON = () => {
    if (!strategy) return;
    
    const dataStr = JSON.stringify(strategy, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `gamification-strategy-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printContent = generateFullPrintHTML(strategy!, assessmentData, tier);
    // Sanitize the full HTML to prevent XSS from AI-generated content
    const sanitizedContent = sanitizeFullHtml(printContent);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(sanitizedContent);
      printWindow.document.close();
      printWindow.addEventListener('load', () => {
        setTimeout(() => printWindow.print(), 300);
      });
    }
  };

  const handleViewDocument = () => {
    const htmlContent = generateFullPrintHTML(strategy!, assessmentData, tier);
    // Sanitize the full HTML to prevent XSS from AI-generated content
    const sanitizedContent = sanitizeFullHtml(htmlContent);
    const blob = new Blob([sanitizedContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const handleDownloadPDF = async () => {
    const htmlContent = generateFullPrintHTML(strategy!, assessmentData, tier);
    // Sanitize the full HTML to prevent XSS from AI-generated content
    const sanitizedContent = sanitizeFullHtml(htmlContent);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(sanitizedContent);
      printWindow.document.close();
      // Add instruction for user - this is safe static content
      const instructionDiv = printWindow.document.createElement('div');
      instructionDiv.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; background: #7c3aed; color: white; padding: 12px 20px; text-align: center; z-index: 9999; font-family: system-ui, sans-serif;';
      
      const textNode = document.createTextNode('To save as PDF: Press Ctrl+P (or Cmd+P on Mac) → Select "Save as PDF" as the destination → Click Save');
      const strong = document.createElement('strong');
      strong.textContent = 'To save as PDF: ';
      
      const button = printWindow.document.createElement('button');
      button.textContent = 'Print / Save as PDF';
      button.style.cssText = 'margin-left: 20px; background: white; color: #7c3aed; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer; font-weight: bold;';
      button.addEventListener('click', () => {
        instructionDiv.remove();
        printWindow.print();
      });
      
      instructionDiv.appendChild(strong);
      instructionDiv.appendChild(document.createTextNode('Press Ctrl+P (or Cmd+P on Mac) → Select "Save as PDF" as the destination → Click Save'));
      instructionDiv.appendChild(button);
      
      printWindow.document.body.insertBefore(instructionDiv, printWindow.document.body.firstChild);
    }
  };

  const generateFullPrintHTML = (strategy: Strategy, data: AssessmentData, tierParam?: string) => {
    const effectiveTier = tierParam || strategy?.recommended_tier || "core";
    
    const implementationGuides = {
      core: {
        technical: [
          "Implement basic progress bars and completion indicators",
          "Set up achievement/goal tracking system",
          "Create simple reward notification system",
          "Design clear visual feedback for actions",
        ],
        design: [
          "Use clear, consistent progress indicators",
          "Implement straightforward color coding for status",
          "Create simple, uncluttered UI elements",
          "Focus on clarity and ease of use",
        ],
        transitions: [
          "Consider Moderate tier when user engagement plateaus",
          "Upgrade if users request social features",
          "Move up when retention metrics stabilize",
        ],
      },
      moderate: {
        technical: [
          "Integrate social sharing and comparison features",
          "Implement achievement badge system",
          "Add personalization engine for content",
          "Set up basic audio feedback system",
          "Create user profile and history tracking",
        ],
        design: [
          "Design engaging achievement badges and icons",
          "Create social comparison widgets",
          "Implement personalized UI themes",
          "Add subtle audio cues for actions",
          "Design progress visualization charts",
        ],
        transitions: [
          "Consider High tier when community forms naturally",
          "Upgrade if users engage heavily with social features",
          "Move up when ready for competitive elements",
        ],
      },
      high: {
        technical: [
          "Build comprehensive leaderboard system",
          "Implement multi-sensory feedback (audio + haptic)",
          "Create advanced analytics dashboard",
          "Set up community features and forums",
          "Develop adaptive difficulty algorithms",
          "Integrate rich notification system",
        ],
        design: [
          "Create immersive multi-sensory experiences",
          "Design competitive leaderboard interfaces",
          "Build sophisticated progress visualizations",
          "Implement dynamic UI that adapts to user",
          "Create rich community interaction spaces",
        ],
        transitions: [
          "Maintain and optimize at this level",
          "Focus on retention and community growth",
          "Consider new features based on analytics",
        ],
      },
    };

    const currentGuide = implementationGuides[effectiveTier as keyof typeof implementationGuides] || implementationGuides.core;

    const renderElements = (elements: StrategyElement[] | undefined, title: string) => {
      if (!elements?.length) return '';
      return `
        <div class="section">
          <h2>${escapeHtml(title)} (${elements.length})</h2>
          ${elements.map(el => `
            <div class="element">
              <div class="element-header">
                <h3>${escapeHtml(el.element)}</h3>
                <span class="priority ${escapeHtml(el.priority.toLowerCase())}">${escapeHtml(el.priority)}</span>
              </div>
              <p>${escapeHtml(el.description)}</p>
              ${el.psychological_basis ? `<p class="detail">🧠 ${escapeHtml(el.psychological_basis)}</p>` : ''}
              ${el.implementation_tips ? `<p class="detail">💡 ${escapeHtml(el.implementation_tips)}</p>` : ''}
            </div>
          `).join('')}
        </div>
      `;
    };

    return `
<!DOCTYPE html>
<html>
<head>
  <title>Gamification Strategy & Implementation - ${data.name || 'Your Strategy'}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #1f2937; max-width: 800px; margin: 0 auto; padding: 40px 20px; }
    .header { background: linear-gradient(135deg, #7c3aed, #4f46e5); color: white; padding: 30px; border-radius: 12px; margin-bottom: 24px; }
    .header h1 { font-size: 28px; margin-bottom: 8px; }
    .header p { opacity: 0.9; margin: 4px 0; }
    .section { background: #fff; border: 1px solid #e5e7eb; padding: 24px; border-radius: 8px; margin-bottom: 20px; break-inside: avoid; }
    .section h2 { color: #7c3aed; font-size: 20px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 16px; }
    .tier-badge { display: inline-block; background: #7c3aed; color: white; padding: 8px 16px; border-radius: 6px; font-weight: bold; text-transform: uppercase; font-size: 14px; }
    .element { padding: 16px; background: #f9fafb; border-left: 4px solid #7c3aed; border-radius: 0 8px 8px 0; margin: 12px 0; }
    .element-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; flex-wrap: wrap; gap: 8px; }
    .element h3 { font-size: 16px; color: #1f2937; }
    .element p { color: #4b5563; font-size: 14px; margin: 8px 0; }
    .element .detail { font-size: 13px; color: #6b7280; }
    .priority { padding: 4px 10px; border-radius: 4px; font-size: 11px; font-weight: bold; text-transform: uppercase; }
    .priority.high { background: #fef2f2; color: #dc2626; }
    .priority.medium { background: #fffbeb; color: #d97706; }
    .priority.low { background: #f0fdf4; color: #16a34a; }
    .profile-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
    .profile-item h4 { font-size: 12px; color: #6b7280; text-transform: uppercase; margin-bottom: 4px; }
    .profile-item p { font-size: 14px; }
    .badge { display: inline-block; background: #e5e7eb; padding: 4px 8px; border-radius: 4px; font-size: 12px; margin: 2px; }
    .anti-pattern { padding: 12px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; margin: 10px 0; }
    .anti-pattern h4 { color: #dc2626; font-size: 14px; margin-bottom: 4px; }
    .anti-pattern p { color: #7f1d1d; font-size: 13px; }
    .metric { padding: 12px; background: #f9fafb; border-radius: 6px; margin: 8px 0; }
    .metric h4 { font-size: 14px; margin-bottom: 4px; }
    .metric p { font-size: 13px; color: #6b7280; }
    .phase { padding: 16px; background: #f9fafb; border-left: 4px solid #7c3aed; border-radius: 0 6px 6px 0; margin: 12px 0; }
    .phase-header { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
    .phase-num { width: 24px; height: 24px; background: #7c3aed; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; }
    ul { padding-left: 20px; }
    li { margin: 8px 0; color: #4b5563; }
    .impl-section { background: linear-gradient(135deg, #f0fdf4, #ecfdf5); border: 1px solid #bbf7d0; }
    .impl-section h2 { color: #16a34a; border-bottom-color: #bbf7d0; }
    .impl-item { padding: 10px 16px; background: white; border-left: 4px solid #16a34a; border-radius: 0 6px 6px 0; margin: 10px 0; }
    .page-break { page-break-before: always; margin-top: 40px; padding-top: 20px; border-top: 2px dashed #e5e7eb; }
    .footer { text-align: center; color: #9ca3af; font-size: 12px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; }
    @media print { 
      body { padding: 20px; } 
      .header, .impl-section { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .section { box-shadow: none; }
      .page-break { page-break-before: always; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>🎯 Your Gamification Strategy</h1>
    <p><strong>${escapeHtml(data.name) || 'Customer'}</strong></p>
    <p>${escapeHtml(data.email)}</p>
    <p>Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
  </div>

  <div class="section">
    <h2>🏆 Recommended Tier</h2>
    <span class="tier-badge">${escapeHtml(strategy.recommended_tier)}</span>
    <p style="margin-top: 16px;">${escapeHtml(strategy.tier_justification)}</p>
  </div>

  ${strategy.psychological_profile ? `
    <div class="section">
      <h2>🧠 Psychological Profile</h2>
      <div class="profile-grid">
        <div class="profile-item">
          <h4>Octalysis Drives</h4>
          <div>${strategy.psychological_profile.primary_octalysis_drives?.map(d => `<span class="badge">${escapeHtml(d)}</span>`).join('') || 'N/A'}</div>
        </div>
        <div class="profile-item">
          <h4>Bartle Type</h4>
          <p>${escapeHtml(strategy.psychological_profile.bartle_type) || 'N/A'}</p>
        </div>
        <div class="profile-item">
          <h4>SDT Focus</h4>
          <p>${escapeHtml(strategy.psychological_profile.sdt_focus) || 'N/A'}</p>
        </div>
        <div class="profile-item">
          <h4>Flow Considerations</h4>
          <p>${escapeHtml(strategy.psychological_profile.flow_considerations) || 'N/A'}</p>
        </div>
      </div>
    </div>
  ` : ''}

  ${strategy.app_profile_summary ? `
    <div class="section">
      <h2>📱 App Profile Summary</h2>
      <div class="profile-grid">
        <div class="profile-item">
          <h4>App Type</h4>
          <p>${escapeHtml(strategy.app_profile_summary.app_type) || 'N/A'}</p>
        </div>
        <div class="profile-item">
          <h4>Target Frequency</h4>
          <p>${escapeHtml(strategy.app_profile_summary.target_frequency) || 'N/A'}</p>
        </div>
        <div class="profile-item">
          <h4>Session Length</h4>
          <p>${escapeHtml(strategy.app_profile_summary.session_length) || 'N/A'}</p>
        </div>
        <div class="profile-item">
          <h4>Primary Challenge</h4>
          <p>${escapeHtml(strategy.app_profile_summary.primary_challenge) || 'N/A'}</p>
        </div>
      </div>
      ${strategy.app_profile_summary.key_motivations?.length ? `
        <div style="margin-top: 16px;">
          <h4 style="font-size: 12px; color: #6b7280; text-transform: uppercase; margin-bottom: 8px;">Key Motivations</h4>
          <div>${strategy.app_profile_summary.key_motivations.map(m => `<span class="badge">${escapeHtml(m)}</span>`).join('')}</div>
        </div>
      ` : ''}
    </div>
  ` : ''}

  ${renderElements(strategy.core_elements, '🎯 Core Elements')}
  ${renderElements(strategy.moderate_elements, '⚡ Moderate Elements')}
  ${renderElements(strategy.advanced_elements, '🏆 Advanced Elements')}

  ${strategy.anti_patterns_to_avoid?.length ? `
    <div class="section">
      <h2>🛡️ Anti-Patterns to Avoid</h2>
      ${strategy.anti_patterns_to_avoid.map(ap => `
        <div class="anti-pattern">
          <h4>⚠️ ${escapeHtml(ap.pattern)}</h4>
          <p>${escapeHtml(ap.reason)}</p>
        </div>
      `).join('')}
    </div>
  ` : ''}

  ${strategy.metrics_to_track?.length ? `
    <div class="section">
      <h2>📊 Metrics to Track</h2>
      ${strategy.metrics_to_track.map(m => `
        <div class="metric">
          <h4>${escapeHtml(m.metric)}</h4>
          <p>${escapeHtml(m.why)}</p>
          ${m.target ? `<p><strong>Target:</strong> ${escapeHtml(m.target)}</p>` : ''}
        </div>
      `).join('')}
    </div>
  ` : ''}

  ${strategy.implementation_timeline ? `
    <div class="section">
      <h2>📅 Implementation Timeline</h2>
      ${Object.entries(strategy.implementation_timeline).map(([key, phase], i) => {
        if (!phase) return '';
        return `
          <div class="phase">
            <div class="phase-header">
              <span class="phase-num">${i + 1}</span>
              <strong>Phase ${i + 1}</strong>
              <span class="badge">Weeks ${escapeHtml(phase.weeks)}</span>
            </div>
            <p>${escapeHtml(phase.focus)}</p>
            <p style="margin-top: 8px; font-size: 13px; color: #7c3aed;">🎯 Success: ${escapeHtml(phase.success_criteria)}</p>
          </div>
        `;
      }).join('')}
    </div>
  ` : ''}

  ${strategy.key_considerations?.length ? `
    <div class="section">
      <h2>💡 Key Considerations</h2>
      <ul>${strategy.key_considerations.map(c => `<li>${escapeHtml(c)}</li>`).join('')}</ul>
    </div>
  ` : ''}

  ${strategy.personalization_notes ? `
    <div class="section">
      <h2>✨ Personalization Notes</h2>
      <p>${escapeHtml(strategy.personalization_notes)}</p>
    </div>
  ` : ''}

  ${strategy.research_references?.length ? `
    <div class="section">
      <h2>📚 Research References</h2>
      ${strategy.research_references.map(ref => `
        <div class="metric">
          <h4>${escapeHtml(ref.concept)}</h4>
          <p><strong>Source:</strong> ${escapeHtml(ref.source)}</p>
          <p><strong>Application:</strong> ${escapeHtml(ref.application)}</p>
        </div>
      `).join('')}
    </div>
  ` : ''}

  <!-- Implementation Guide Section -->
  <div class="page-break">
    <div class="header" style="background: linear-gradient(135deg, #16a34a, #059669);">
      <h1>🔧 Implementation Guide</h1>
      <p>Technical specifications and design guidelines for your <strong>${escapeHtml(effectiveTier)}</strong> tier strategy</p>
    </div>
  </div>

  <div class="section impl-section">
    <h2>⚙️ Technical Specifications</h2>
    ${currentGuide.technical.map(spec => `
      <div class="impl-item">
        <p>${spec}</p>
      </div>
    `).join('')}
  </div>

  <div class="section impl-section">
    <h2>🎨 Design Guidelines</h2>
    ${currentGuide.design.map(guideline => `
      <div class="impl-item">
        <p>${guideline}</p>
      </div>
    `).join('')}
  </div>

  <div class="section" style="background: linear-gradient(135deg, #fefce8, #fef9c3); border-color: #fde047;">
    <h2 style="color: #ca8a04; border-bottom-color: #fde047;">⏰ When to Transition Tiers</h2>
    ${currentGuide.transitions.map(transition => `
      <div class="impl-item" style="border-left-color: #ca8a04;">
        <p>${transition}</p>
      </div>
    `).join('')}
  </div>

  <div class="section">
    <h2>✅ Best Practices</h2>
    <ul>
      <li><strong>Start simple:</strong> Implement core features first, then iterate based on user feedback</li>
      <li><strong>Test extensively:</strong> A/B test different gamification elements to find what resonates</li>
      <li><strong>Monitor metrics:</strong> Track engagement, retention, and user satisfaction continuously</li>
      <li><strong>Stay flexible:</strong> Be ready to adjust your strategy based on real user behavior</li>
    </ul>
  </div>

  <div class="footer">
    <p>Strategy generated based on your assessment responses.</p>
    <p>© ${new Date().getFullYear()} GamifyMe Scorecard</p>
  </div>
</body>
</html>
    `;
  };

  const renderElementCard = (element: StrategyElement, index: number) => {
    const priorityStyles = {
      high: "bg-purple-600/15 text-purple-700 dark:text-purple-300 border-purple-600/30",
      medium: "bg-blue-500/15 text-blue-600 dark:text-blue-300 border-blue-500/30",
      low: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20",
    };
    const priorityClass = priorityStyles[element.priority as keyof typeof priorityStyles] || priorityStyles.medium;
    
    return (
      <Card key={index} className="p-4 bg-card/50 border border-border/50">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="font-semibold">{element.element}</h4>
          <Badge className={`text-xs capitalize border ${priorityClass}`}>
            {element.priority.charAt(0).toUpperCase() + element.priority.slice(1)}
          </Badge>
        </div>
      <p className="text-sm text-muted-foreground mb-2">{element.description}</p>
      {element.psychological_basis && (
        <p className="text-xs text-primary/80 mb-2">
          <Brain className="w-3 h-3 inline mr-1" />
          {element.psychological_basis}
        </p>
      )}
      {element.implementation_tips && (
        <p className="text-xs text-muted-foreground">
          <Lightbulb className="w-3 h-3 inline mr-1" />
          {element.implementation_tips}
        </p>
      )}
      {element.caution_notes && (
        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
          <AlertTriangle className="w-3 h-3 inline mr-1" />
          {element.caution_notes}
        </p>
      )}
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12 px-4">
      <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br ${currentTier.bg} mb-4`}>
            <Icon className={`w-10 h-10 ${currentTier.color}`} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold">Your Gamification Strategy</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {strategy?.tier_justification || "Based on your responses, here's your personalized gamification strategy"}
          </p>
        </div>

        {/* Tier Recommendation */}
        <Card className="p-8 shadow-lg border-2 border-primary/20">
          <div className="flex items-center gap-4 mb-4">
            <Badge className={`text-lg px-4 py-2 ${currentTier.color} bg-transparent border-2`}>
              {currentTier.title}
            </Badge>
          </div>
          {strategy?.tier_justification && (
            <p className="text-muted-foreground">{strategy.tier_justification}</p>
          )}
        </Card>

        {/* Psychological Profile */}
        {strategy?.psychological_profile && (
          <Card className="p-8 shadow-lg bg-gradient-to-br from-blue-500/10 to-blue-600/5">
            <div className="flex items-center gap-3 mb-6">
              <Brain className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">Psychological Profile</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-2 text-muted-foreground">Primary Octalysis Drives</h3>
                <div className="flex flex-wrap gap-2">
                  {strategy.psychological_profile.primary_octalysis_drives?.map((drive, i) => (
                    <Badge key={i} variant="secondary">{drive}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2 text-muted-foreground">Bartle Player Type</h3>
                <p className="text-lg">{strategy.psychological_profile.bartle_type}</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2 text-muted-foreground">Self-Determination Focus</h3>
                <p className="text-lg">{strategy.psychological_profile.sdt_focus}</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2 text-muted-foreground">Flow State Considerations</h3>
                <p className="text-lg">{strategy.psychological_profile.flow_considerations}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Strategy Elements Accordion */}
        <Accordion type="multiple" defaultValue={["core", "moderate"]} className="space-y-4">
          {/* Core Elements */}
          {strategy?.core_elements && strategy.core_elements.length > 0 && (
            <AccordionItem value="core" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-bold">
                <div className="flex items-center gap-3">
                  <Target className="w-5 h-5 text-primary" />
                  Core Elements ({strategy.core_elements.length})
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid md:grid-cols-2 gap-4 pt-4">
                  {strategy.core_elements.map((el, i) => renderElementCard(el, i))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Moderate Elements */}
          {strategy?.moderate_elements && strategy.moderate_elements.length > 0 && (
            <AccordionItem value="moderate" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-bold">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-accent" />
                  Moderate Elements ({strategy.moderate_elements.length})
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid md:grid-cols-2 gap-4 pt-4">
                  {strategy.moderate_elements.map((el, i) => renderElementCard(el, i))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Advanced Elements */}
          {strategy?.advanced_elements && strategy.advanced_elements.length > 0 && (
            <AccordionItem value="advanced" className="border rounded-lg px-6">
              <AccordionTrigger className="text-xl font-bold">
                <div className="flex items-center gap-3">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Advanced Elements ({strategy.advanced_elements.length})
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid md:grid-cols-2 gap-4 pt-4">
                  {strategy.advanced_elements.map((el, i) => renderElementCard(el, i))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>

        {/* Anti-Patterns */}
        {strategy?.anti_patterns_to_avoid && strategy.anti_patterns_to_avoid.length > 0 && (
          <Card className="p-8 shadow-lg border-destructive/20">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-destructive" />
              <h2 className="text-2xl font-bold">Anti-Patterns to Avoid</h2>
            </div>
            <div className="space-y-4">
              {strategy.anti_patterns_to_avoid.map((ap, i) => (
                <div key={i} className="flex gap-3 p-4 bg-destructive/5 rounded-lg border border-destructive/20">
                  <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-destructive">{ap.pattern}</h4>
                    <p className="text-sm text-muted-foreground">{ap.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Metrics to Track */}
        {strategy?.metrics_to_track && strategy.metrics_to_track.length > 0 && (
          <Card className="p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">Metrics to Track</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {strategy.metrics_to_track.map((metric, i) => (
                <Card key={i} className="p-4 bg-card/50">
                  <h4 className="font-semibold mb-1">{metric.metric}</h4>
                  <p className="text-sm text-muted-foreground mb-2">{metric.why}</p>
                  {metric.target && (
                    <Badge variant="outline" className="text-xs">Target: {metric.target}</Badge>
                  )}
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Implementation Timeline */}
        {strategy?.implementation_timeline && (
          <Card className="p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <Calendar className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">Implementation Timeline</h2>
            </div>
            <div className="space-y-6">
              {Object.entries(strategy.implementation_timeline).map(([phase, data], i) => {
                if (!data) return null;
                const phaseData = data as TimelinePhase;
                return (
                  <div key={phase} className="relative pl-8 border-l-2 border-primary/30">
                    <div className="absolute -left-2.5 top-0 w-5 h-5 rounded-full bg-primary flex items-center justify-center text-xs text-primary-foreground font-bold">
                      {i + 1}
                    </div>
                    <div className="pb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-lg">Phase {i + 1}</h4>
                        <Badge variant="secondary">Weeks {phaseData.weeks}</Badge>
                      </div>
                      <p className="text-muted-foreground mb-3">{phaseData.focus}</p>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {phaseData.elements?.map((el, j) => (
                          <Badge key={j} variant="outline">{el}</Badge>
                        ))}
                      </div>
                      <p className="text-sm text-primary/80">
                        <Gauge className="w-3 h-3 inline mr-1" />
                        Success: {phaseData.success_criteria}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Research References */}
        {strategy?.research_references && strategy.research_references.length > 0 && (
          <Card className="p-8 shadow-lg bg-gradient-to-br from-accent/5 to-primary/10">
            <div className="flex items-center gap-3 mb-6">
              <BookOpen className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">Research & Framework References</h2>
            </div>
            <div className="space-y-4">
              {strategy.research_references.map((ref, i) => (
                <div key={i} className="p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Badge variant="secondary" className="shrink-0">{ref.source}</Badge>
                    <div>
                      <h4 className="font-semibold">{ref.concept}</h4>
                      <p className="text-sm text-muted-foreground">{ref.application}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Personalization Notes */}
        {strategy?.personalization_notes && (
          <Card className="p-8 shadow-lg bg-gradient-to-br from-primary/5 to-accent/5">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">Personalization Notes</h2>
            </div>
            <p className="text-muted-foreground">{strategy.personalization_notes}</p>
          </Card>
        )}

        {/* Key Considerations */}
        {strategy?.key_considerations && strategy.key_considerations.length > 0 && (
          <Card className="p-8 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">Key Considerations</h2>
            <ul className="space-y-2">
              {strategy.key_considerations.map((consideration, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                  <span className="text-muted-foreground">{consideration}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Your Profile Summary */}
        <Card className="p-8 shadow-lg bg-gradient-to-br from-primary/5 to-accent/10">
          <h2 className="text-2xl font-bold mb-6">Your Assessment Profile</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-2 text-muted-foreground">App Type</h3>
              <p className="text-lg">{assessmentData.appType}</p>
            </div>
            <div>
              <h3 className="font-semibold mb-2 text-muted-foreground">Experience Intent</h3>
              <p className="text-lg">{assessmentData.experienceIntent}</p>
            </div>
            <div>
              <h3 className="font-semibold mb-2 text-muted-foreground">Usage Frequency</h3>
              <p className="text-lg">{assessmentData.usageFrequency}</p>
            </div>
            <div>
              <h3 className="font-semibold mb-2 text-muted-foreground">Session Duration</h3>
              <p className="text-lg">{assessmentData.sessionDuration} minutes</p>
            </div>
          </div>

          {assessmentData.userMotivations.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold mb-3 text-muted-foreground">Key Motivations</h3>
              <div className="flex flex-wrap gap-2">
                {assessmentData.userMotivations.map((motivation) => (
                  <Badge key={motivation} variant="secondary">
                    {motivation}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {assessmentData.sensoryPreferences.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold mb-3 text-muted-foreground">Sensory Elements</h3>
              <div className="flex flex-wrap gap-2">
                {assessmentData.sensoryPreferences.map((element) => (
                  <Badge key={element} variant="secondary">
                    {element}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
          <Button
            size="lg"
            onClick={() => navigate("/implementation", { state: { tier, assessmentData, strategy } })}
            className="text-lg px-8 py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
          >
            View Implementation Details
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          {strategy && (
            <Button
              size="lg"
              variant="outline"
              onClick={handlePrint}
              className="text-lg px-8 py-6"
            >
              <Printer className="w-5 h-5 mr-2" />
              Print Strategy
            </Button>
          )}
          {strategy && (
            <Button
              size="lg"
              variant="outline"
              onClick={handleViewDocument}
              className="text-lg px-8 py-6"
            >
              <FileText className="w-5 h-5 mr-2" />
              View Document
            </Button>
          )}
          {strategy && (
            <Button
              size="lg"
              variant="outline"
              onClick={handleDownloadPDF}
              className="text-lg px-8 py-6"
            >
              <Download className="w-5 h-5 mr-2" />
              Download PDF
            </Button>
          )}
          {strategy && (
            <Button
              size="lg"
              variant="ghost"
              onClick={handleDownloadJSON}
              className="text-lg px-8 py-6"
            >
              <Download className="w-5 h-5 mr-2" />
              Download JSON
            </Button>
          )}
          <Button
            size="lg"
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-lg px-8 py-6"
          >
            Return to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Results;
